import { i18n, t } from '@/i18n';
import { normalizeSiteLocale, type SiteLocale } from '@/lib/site-locale';

export interface LearnChapter {
  slug: string;
  title: string;
  heading: string;
  description?: string;
  hidden?: boolean;
  load: () => Promise<{ default: string }>;
}

/** Map `/learn/[slug]` to i18n key segment (e.g. magic-play → magic_play). */
export function learnChapterI18nKey(slug: string) {
  return slug.replace(/-/g, '_');
}

type MarkdownLoader = () => Promise<{ default: string }>;

const learnMarkdownBySlug: Record<
  string,
  {
    en: MarkdownLoader;
    ja?: MarkdownLoader;
    ko?: MarkdownLoader;
    es?: MarkdownLoader;
  }
> = {
  welcome: {
    en: () => import('@/content/blog/00-intro.md?raw'),
    ja: () => import('@/content/blog/ja/00-intro.md?raw'),
    ko: () => import('@/content/blog/ko/00-intro.md?raw'),
    es: () => import('@/content/blog/es/00-intro.md?raw'),
  },
  quickstart: {
    en: () => import('@/content/blog/01-quickstart.md?raw'),
    ja: () => import('@/content/blog/ja/01-quickstart.md?raw'),
    ko: () => import('@/content/blog/ko/01-quickstart.md?raw'),
    es: () => import('@/content/blog/es/01-quickstart.md?raw'),
  },
  prompt: {
    en: () => import('@/content/blog/02-prompt.md?raw'),
    ja: () => import('@/content/blog/ja/02-prompt.md?raw'),
    ko: () => import('@/content/blog/ko/02-prompt.md?raw'),
    es: () => import('@/content/blog/es/02-prompt.md?raw'),
  },
  editor: {
    en: () => import('@/content/blog/03-editor.md?raw'),
    ja: () => import('@/content/blog/ja/03-editor.md?raw'),
    ko: () => import('@/content/blog/ko/03-editor.md?raw'),
    es: () => import('@/content/blog/es/03-editor.md?raw'),
  },
  export: {
    en: () => import('@/content/blog/04-export.md?raw'),
    ja: () => import('@/content/blog/ja/04-export.md?raw'),
    ko: () => import('@/content/blog/ko/04-export.md?raw'),
    es: () => import('@/content/blog/es/04-export.md?raw'),
  },
  credits: {
    en: () => import('@/content/blog/05-credits.md?raw'),
    ja: () => import('@/content/blog/ja/05-credits.md?raw'),
    ko: () => import('@/content/blog/ko/05-credits.md?raw'),
    es: () => import('@/content/blog/es/05-credits.md?raw'),
  },
  'magic-play': {
    en: () => import('@/content/blog/12-magic-play.md?raw'),
    ja: () => import('@/content/blog/ja/12-magic-play.md?raw'),
    ko: () => import('@/content/blog/ko/12-magic-play.md?raw'),
    es: () => import('@/content/blog/es/12-magic-play.md?raw'),
  },
  faq: {
    en: () => import('@/content/blog/07-faq.md?raw'),
    ja: () => import('@/content/blog/ja/07-faq.md?raw'),
    ko: () => import('@/content/blog/ko/07-faq.md?raw'),
    es: () => import('@/content/blog/es/07-faq.md?raw'),
  },
  'save-sync': {
    en: () => import('@/content/blog/08-save-sync.md?raw'),
    ja: () => import('@/content/blog/ja/08-save-sync.md?raw'),
    ko: () => import('@/content/blog/ko/08-save-sync.md?raw'),
    es: () => import('@/content/blog/es/08-save-sync.md?raw'),
  },
  troubleshoot: {
    en: () => import('@/content/blog/08-troubleshoot.md?raw'),
    ja: () => import('@/content/blog/ja/08-troubleshoot.md?raw'),
    ko: () => import('@/content/blog/ko/08-troubleshoot.md?raw'),
    es: () => import('@/content/blog/es/08-troubleshoot.md?raw'),
  },
  shortcuts: {
    en: () => import('@/content/blog/09-shortcuts.md?raw'),
    ja: () => import('@/content/blog/ja/09-shortcuts.md?raw'),
    ko: () => import('@/content/blog/ko/09-shortcuts.md?raw'),
    es: () => import('@/content/blog/es/09-shortcuts.md?raw'),
  },
  api: {
    en: () => import('@/content/blog/11-api.md?raw'),
    ja: () => import('@/content/blog/ja/11-api.md?raw'),
    ko: () => import('@/content/blog/ko/11-api.md?raw'),
    es: () => import('@/content/blog/es/11-api.md?raw'),
  },
};

export async function loadLearnChapterMarkdown(slug: string): Promise<string> {
  const files = learnMarkdownBySlug[slug];
  if (!files) {
    throw new Error(`Unknown learn slug: ${slug}`);
  }

  const locale = normalizeSiteLocale(i18n.locale) ?? 'en';
  const loader = files[locale as SiteLocale] || files.en;
  const mod = await loader();
  return mod.default;
}

function createLearnLoader(slug: string): () => Promise<{ default: string }> {
  return async () => ({ default: await loadLearnChapterMarkdown(slug) });
}

export const learnChapters: LearnChapter[] = [
  {
    slug: 'welcome',
    title: 'Welcome',
    heading: 'Welcome to Veeso',
    description:
      'Learn what Veeso does, how it turns content into editable designs, and where to start.',
    load: createLearnLoader('welcome'),
  },
  {
    slug: 'quickstart',
    title: 'Quick Start',
    heading: 'Create Your First Editable AI Design Fast',
    description:
      'Create your first editable AI design with Veeso: choose a format, add content and references, generate, edit text and visuals, then export.',
    load: createLearnLoader('quickstart'),
  },
  {
    slug: 'prompt',
    title: 'Prompt Guide',
    heading: 'Write Better Inputs, Get Better Layouts',
    description:
      'Improve Veeso results with better prompts, references, files, and structured content inputs.',
    load: createLearnLoader('prompt'),
  },
  {
    slug: 'editor',
    title: 'Editor Guide',
    heading: 'How the Veeso Editor Works',
    description:
      'Explore the Veeso editor interface, AI chat, pages panel, text editing, image tools, and export workflow.',
    load: createLearnLoader('editor'),
  },
  {
    slug: 'export',
    title: 'Export',
    heading: 'Export and Deliver Your Work',
    description:
      'Export Veeso projects as PNG, JPG, WebP, PDF, PPT, MP4, SVG, or HTML and fix common export issues.',
    load: createLearnLoader('export'),
  },
  {
    slug: 'credits',
    title: 'Credits',
    heading: 'Credits, Billing, and Plans',
    description:
      'Understand Veeso credits, daily free credits, paid credits, generation costs, refunds, and billing behavior.',
    load: createLearnLoader('credits'),
  },
  {
    slug: 'magic-play',
    title: 'Creative Ideas',
    heading: 'Creative Workflows and Use Cases',
    description:
      'Explore creative workflows and practical use cases for turning ideas and content into Veeso designs.',
    hidden: true,
    load: createLearnLoader('magic-play'),
  },
  {
    slug: 'faq',
    title: 'FAQ',
    heading: 'Frequently Asked Questions',
    description:
      'Find quick answers about Veeso accounts, credits, exports, editing, support, and common product questions.',
    load: createLearnLoader('faq'),
  },
  {
    slug: 'save-sync',
    title: 'Save and Sync',
    heading: 'How Saving and Sync Work',
    description:
      'Learn how Veeso saves projects, syncs changes, handles version history, and keeps work recoverable.',
    load: createLearnLoader('save-sync'),
  },
  {
    slug: 'troubleshoot',
    title: 'Troubleshooting',
    heading: 'Fix Common Issues Fast',
    description:
      'Fix common Veeso issues including blank canvas, stuck generation, failed rendering, export errors, and editing problems.',
    load: createLearnLoader('troubleshoot'),
  },
  {
    slug: 'shortcuts',
    title: 'Shortcuts',
    heading: 'Keyboard Shortcuts',
    description:
      'Use Veeso keyboard shortcuts to edit faster, navigate the canvas, and work more efficiently.',
    load: createLearnLoader('shortcuts'),
  },
  {
    slug: 'api',
    title: 'API',
    heading: 'API Access',
    description: 'Learn about Veeso API access and integration options.',
    hidden: true,
    load: createLearnLoader('api'),
  },
];

export const learnNavSections = [
  { id: 'start', slugs: ['welcome', 'quickstart'] },
  { id: 'create', slugs: ['prompt', 'editor', 'magic-play'] },
  { id: 'ship', slugs: ['export', 'credits', 'save-sync'] },
  { id: 'help', slugs: ['faq', 'troubleshoot', 'shortcuts'] },
];

export const visibleLearnChapters = learnChapters.filter((chapter) => !chapter.hidden);

export const learnDocSlugs = visibleLearnChapters.map((chapter) => chapter.slug);

export function learnChapterBySlug(slug?: string | null) {
  return learnChapters.find((chapter) => chapter.slug === slug);
}

/** Sidebar chapter link label — shell i18n with English fallback from learnChapters. */
export function learnChapterTitle(slug: string) {
  const localized = t(`landingPages.learn.chapter.${learnChapterI18nKey(slug)}.title`);
  if (localized) return localized;
  return learnChapterBySlug(slug)?.title || slug;
}

/** Page H1 — shell i18n with English fallback from learnChapters. */
export function learnChapterHeading(slug: string) {
  const localized = t(`landingPages.learn.chapter.${learnChapterI18nKey(slug)}.heading`);
  if (localized) return localized;
  return learnChapterBySlug(slug)?.heading || slug;
}

/** SEO meta description — shell i18n with English fallback from learnChapters. */
export function learnChapterDescription(slug: string) {
  const localized = t(`landingPages.learn.chapter.${learnChapterI18nKey(slug)}.description`);
  if (localized) return localized;
  return learnChapterBySlug(slug)?.description || '';
}

/** Sidebar section heading — shell i18n. */
export function learnNavSectionLabel(sectionId: string) {
  return t(`landingPages.learn.nav.section.${sectionId}`);
}

export function slugifyHeading(text: string) {
  return text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
}

function stripHeadingMarkdown(raw: string) {
  return raw
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .trim();
}

const BLOCKQUOTE_H2 = /^\s*(?:>\s*)+##\s+/;

export function extractLearnHeadings(source: string) {
  if (!source) return [];

  const headings: Array<{ text: string; id: string }> = [];

  for (const line of source.split(/\r?\n/)) {
    if (BLOCKQUOTE_H2.test(line)) continue;
    const match = line.match(/^##\s+(.+)$/);
    if (!match) continue;

    const text = stripHeadingMarkdown(match[1]);
    if (!text) continue;

    headings.push({
      text,
      id: slugifyHeading(text),
    });
  }

  return headings;
}
